import {
  AnimatedCounter,
  cn,
  GlassCard,
  GlassCardContent,
  SkeletonLoader,
} from '@merritrac/shared/components';

import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

interface KPIData {
  label: string;
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  change?: string;
  icon: LucideIcon;
  accentColor?: string;
  highlight?: boolean;
}

interface KPIStripProps {
  items: KPIData[];
  loading?: boolean;
}

function KPICardSkeleton(): ReactElement {
  return (
    <GlassCard>
      <GlassCardContent className="py-4">
        <div className="space-y-3">
          <SkeletonLoader variant="text" width="60%" />
          <SkeletonLoader variant="block" height="32px" width="80px" />
          <SkeletonLoader variant="text" width="40%" />
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

function KPICard({ item }: { item: KPIData }): ReactElement {
  const Icon = item.icon;

  return (
    <GlassCard hover>
      <GlassCardContent className="py-4">
        <div className="flex items-center gap-2 mb-2">
          <div
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-lg shrink-0',
              item.highlight === true ? 'bg-warning-soft' : 'bg-primary-soft',
            )}
          >
            <Icon
              size={14}
              className={item.highlight === true ? 'text-warning-fg' : 'text-primary'}
            />
          </div>
          <p className="text-[11px] font-medium text-text-secondary uppercase tracking-wide leading-tight">
            {item.label}
          </p>
        </div>
        <div>
          <AnimatedCounter
            value={item.value}
            prefix={item.prefix ?? ''}
            suffix={item.suffix ?? ''}
            decimals={item.decimals ?? 0}
            className={cn(
              'text-2xl font-bold',
              item.highlight === true ? 'text-warning-fg' : 'text-text',
            )}
          />
          {item.change !== undefined && (
            <p className="mt-1 text-xs text-text-tertiary">{item.change}</p>
          )}
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

export function KPIStrip({ items, loading }: KPIStripProps): ReactElement {
  if (loading === true) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <KPICardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
      {items.map((item) => (
        <KPICard key={item.label} item={item} />
      ))}
    </div>
  );
}
